"use client";

import { ReactNode, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import * as Sentry from "@sentry/react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

import { IdentityProvider } from "@zapier/identity";
import { UniversalLayout } from "@zapier/universal-layout";
import type { EventNameType } from "@zapier/avro-events";
import { DesignSystemProvider, Spinner } from "@zapier/design-system";

import {
  FullStory,
  TrackRouting,
  initializeDatadogRum,
  initializeSentry,
  nextJsReportWebVitals,
} from "../observability";
import { isDatadog } from "../utils/isDatadog";
import emitEvent from "../utils/emitEvent";
import { SplitProvider } from "./SplitProvider";
import {
  CurrentUserContextProvider,
  useCurrentUserContext,
} from "../contexts/CurrentUserContext";
import { CopilotContextProvider } from "../contexts/CopilotContext";
import { ApiError } from "@/app/app/get-help/utils/apiClient";

interface Props {
  children: ReactNode;
}

interface AppLayoutContentProps extends Props {
  isGetHelpRoute?: boolean;
}

const shouldInitSentry = !isDatadog();
if (shouldInitSentry) {
  initializeSentry({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || "",
    commitHash: process.env.NEXT_PUBLIC_COMMIT_SHA || "",
    environment: process.env.NEXT_PUBLIC_VERCEL_ENV || "",
  });
}

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
initializeDatadogRum();
export const reportWebVitals = nextJsReportWebVitals;

// Create QueryClient outside component to avoid recreating on every render.
// A 401 ApiError from an apiClient-backed query means the session is dead
// (e.g. expired in an open tab) — the proxy routes only return 401 for auth
// failures. Re-fetch currentUser from the authoritative endpoint so the UI
// self-corrects instead of waiting for the auth query's own refetch window.
// Note: this fires only for react-query *queries* that throw an ApiError; today
// that's useAccountEntitlements. It does not force logged-out — the currentUser
// refetch decides — so a spurious 401 at worst causes one redundant re-check.
const queryCache = new QueryCache({
  onError: (error) => {
    if (error instanceof ApiError && error.statusCode === 401) {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 12 * 1000 * 60, // 12 minutes
      cacheTime: 10 * 1000 * 60, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  },
});

function AppLayoutContent({ children, isGetHelpRoute }: AppLayoutContentProps) {
  const pathname = usePathname();
  const isChatWidgetPage = pathname?.startsWith("/app/chat-widget");
  const { user: currentUser, isLoading: isLoadingUser } =
    useCurrentUserContext();

  // Set Sentry user context when user changes
  useEffect(() => {
    if (currentUser?.id) {
      Sentry.setUser({
        id: currentUser.id,
        email: currentUser.email ?? undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [currentUser?.id, currentUser?.email]);

  // Extract the main section from pathname (e.g., /app/get-help -> get-help)
  const mainContentId = pathname?.match(/^\/app\/([^/]+)/)?.[1] || "support";

  // Map user data for FullStory
  const user = currentUser
    ? {
        id: currentUser.id || "", // FullStory requires an id
        fullName: currentUser.full_name || "",
        email: currentUser.email || "",
      }
    : undefined;

  const userLoggedIn = !isLoadingUser && currentUser?.id;

  // Show loading state while fetching user
  if (isLoadingUser) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner />
      </div>
    );
  }

  // For all other routes, use the standard UniversalLayout
  return (
    <IdentityProvider>
      <TrackRouting>
        <SplitProvider userId={currentUser?.id}>
          <UniversalLayout
            currentUrl={pathname || ""}
            mainContentId={mainContentId}
            {...(!userLoggedIn && { mode: "navigation" })}
            {...(isChatWidgetPage && {
              mode: "navigation",
              renderAlertBanner: () => null,
              renderFooter: () => null,
              renderHeader: () => null,
            })}
            trackEvent={(eventName: string, options = {}) => {
              emitEvent(eventName as EventNameType, {
                ...options,
              });
            }}
            showSimplifiedFooter={true}
          >
            {/*
              This nested QueryClientProvider prevents "Oh no, something went wrong" errors
              in the Usage section of the UniversalLayout sidebar. UniversalLayout has its own internal QueryClient,
              and this provider isolates our app's queries from its internals.
            */}
            <QueryClientProvider client={queryClient}>
              {isGetHelpRoute ? (
                <CopilotContextProvider>{children}</CopilotContextProvider>
              ) : (
                children
              )}
            </QueryClientProvider>
            <FullStory user={user} />
          </UniversalLayout>
        </SplitProvider>
      </TrackRouting>
    </IdentityProvider>
  );
}

function AppLayout({ children }: Props) {
  const pathname = usePathname();
  const router = useRouter();
  const isGetHelpRoute = pathname?.includes("/get-help");

  return (
    <DesignSystemProvider navigate={router.push}>
      <QueryClientProvider client={queryClient}>
        <CurrentUserContextProvider>
          <AppLayoutContent isGetHelpRoute={isGetHelpRoute}>
            {children}
          </AppLayoutContent>
        </CurrentUserContextProvider>
      </QueryClientProvider>
    </DesignSystemProvider>
  );
}

export default Sentry.withProfiler(AppLayout);
